<template>
  <ion-footer>
    <ion-toolbar>
      <ion-row>
        <ion-col class="footer-col">
          <div
            role="button"
            tabindex="0"
            :class="{ 'active-tab': isActiveTab('/home') }"
            @click="navigate('/home')"
            @keydown="(e) => handleNavKeydown(e, '/home')"
            class="nav-button"
          >
            <ion-icon :icon="home" class="footer-icon-sharp" />
          </div>
        </ion-col>
        <ion-col class="footer-col">
          <div
            role="button"
            tabindex="0"
            :class="{ 'active-tab': isActiveTab('/search') }"
            @click="navigate('/search')"
            @keydown="(e) => handleNavKeydown(e, '/search')"
            class="nav-button"
          >
            <ion-icon :icon="search" class="footer-icon-sharp" />
          </div>
        </ion-col>
        <ion-col v-if="isLoggedIn" class="footer-col">
          <div
            role="button"
            tabindex="0"
            class="cart-icon-container nav-button"
            :class="{ 'active-tab': isActiveTab('/cart') }"
            @click="navigate('/cart')"
            @keydown="(e) => handleNavKeydown(e, '/cart')"
          >
            <ion-icon :icon="cart" class="footer-icon-sharp" />
          </div>
          <ion-badge
            v-if="cartOrders.length > 0"
            color="danger"
            class="home-notif-margin"
          >
            {{ totalCartItems > 0 ? totalCartItems : "" }}
          </ion-badge>
        </ion-col>
        <ion-col v-if="isLoggedIn" class="footer-col">
          <div
            role="button"
            tabindex="0"
            :class="{ 'active-tab': isFavoritesActive }"
            @click="navigate('/favorites')"
            @keydown="(e) => handleNavKeydown(e, '/favorites')"
            class="nav-button"
          >
            <ion-icon :icon="heart" class="footer-icon-sharp" />
          </div>
        </ion-col>
        <ion-col class="footer-col">
          <div
            class="account-button"
            role="button"
            tabindex="0"
            @click="openMenu"
            @keydown="handleAccountKeydown"
          >
            <ion-icon :icon="person" class="footer-icon-account" />
          </div>
        </ion-col>
      </ion-row>
    </ion-toolbar>
  </ion-footer>
</template>
<script>
import { defineComponent } from "vue";
import {
  IonFooter,
  IonToolbar,
  IonRow,
  IonCol,
  IonBadge,
  IonIcon,
  menuController,
} from "@ionic/vue";
import { person, home, search, cart, heart } from "ionicons/icons";

export default defineComponent({
  name: "TheFooter",
  components: {
    IonFooter,
    IonToolbar,
    IonRow,
    IonCol,
    IonBadge,
    IonIcon,
  },
  data() {
    return {
      person,
      home,
      search,
      cart,
      heart,
    };
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters.isLoggedIn;
    },
    cartOrders() {
      return this.$store.state.cartOrders;
    },
    totalCartItems() {
      // Sum up the quantity from all cart orders (all cafes)
      // Ensure we convert to number to avoid string concatenation
      return this.cartOrders.reduce((total, cart) => {
        return total + (parseInt(cart.quantity) || 0);
      }, 0);
    },
    currentPath() {
      return this.$route?.path || "";
    },
    isFavoritesActive() {
      // Check if we're on the /favorites route
      return this.currentPath === "/favorites";
    },
  },
  methods: {
    isActiveTab(path) {
      return this.currentPath === path;
    },
    navigate(path) {
      if (this.currentPath !== path) {
        this.$router.push(path);
      }
    },
    handleNavKeydown(event, path) {
      // Handle Enter and Space keys for keyboard accessibility
      if (
        event.key === "Enter" ||
        event.key === " " ||
        event.key === "Spacebar"
      ) {
        // Prevent default for Space to avoid scrolling
        if (event.key === " " || event.key === "Spacebar") {
          event.preventDefault();
        }
        this.navigate(path);
      }
    },
    async openMenu() {
      await menuController.open();
    },
    handleAccountKeydown(event) {
      // Handle Enter and Space keys for keyboard accessibility
      if (
        event.key === "Enter" ||
        event.key === " " ||
        event.key === "Spacebar"
      ) {
        // Prevent default for Space to avoid scrolling
        if (event.key === " " || event.key === "Spacebar") {
          event.preventDefault();
        }
        this.openMenu();
      }
    },
  },
  async created() {
    // Only fetch cart orders for logged in customers (not shop admins or anonymous users)
    const userType = localStorage.getItem("userType");

    if (this.isLoggedIn && userType === "customer") {
      await this.$store.dispatch("fetchCartOrders");
    }
  },
});
</script>
<style scoped>
ion-toolbar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: auto;
  min-height: 70px;
  width: 100%;
  background-color: #fff;
  z-index: 999;
  border-top: 1px solid #f3f3f3;
  --padding-top: 16px;
  --padding-bottom: 16px;
}
.footer-icon-sharp {
  font-size: 24px;
  width: 24px;
  height: 24px;
  color: #828282;
  transition: color 0.2s ease;
}
.active-tab .footer-icon-sharp {
  color: #f87e63;
}
.footer-icon-account {
  font-size: 24px;
  width: 24px;
  height: 24px;
  color: #828282;
  transition: color 0.2s ease;
}
.account-button {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  pointer-events: auto;
  position: relative;
  z-index: 1000;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}
.account-button:hover .footer-icon-account {
  color: #f87e63;
}
.account-button:active .footer-icon-account {
  color: #f87e63;
}
.cart-icon-container {
  position: relative;
  display: inline-block;
}
.home-notif-margin {
  position: absolute;
  top: -5px;
  right: -5px;
  transform: translate(0, 0);
}

.footer-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
}

.nav-button {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}

.nav-button:focus-visible {
  outline: 2px solid #f87e63;
  outline-offset: 2px;
  border-radius: 4px;
}
</style>
